import {
  Box,
  Breadcrumbs,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AlertModal from "../../../componentes/alertModal";
import { useStyle } from "./style";
import {
  Add,
  ArrowBackOutlined,
  DeleteOutline,
  EditOutlined,
  Search,
  TuneOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import api from "../../../services/api";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { ptBR } from "@mui/x-data-grid/locales";
import { notifyOnPage } from "../../../utils/notifyOnPage";
import { useNotification } from "../../../componentes/notificationProvider";
import { ReactComponent as EmptyBox } from "../../../assets/easyReport/iconSvg/emptyBox.svg";

interface FunctionsDataType {
  id: string;
  titulo: string;
  descricao: string;
  isDefault?: boolean;
  perfis?: string[];
}

const FunctionPage = () => {
  const theme = useTheme();
  const styles = useStyle(theme);
  const navigate = useNavigate();
  const notify = useNotification();
  const [deleting, setDeleting] = useState(false);

  const [allFunctions, setAllFunctions] = useState<FunctionsDataType[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [idToDelete, setIdToDelete] = useState<string>("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [loading, setLoading] = useState(false);
  const [warningModal, setWarningModal] = useState<boolean>(false);
  const [selectedFunction, setSelectedFunction] =
    useState<FunctionsDataType | null>(null);
  useEffect(() => {
    setLoading(true);
    const getFunctions = async () => {
      try {
        const response = await api.get("/easyReport/v2/findManyFunctions");
        setAllFunctions(response.data);
      } catch (error: any) {
        notify(
          error?.response?.data?.error || "Erro ao carregar funções",
          "error",
          3000,
        );
      } finally {
        setLoading(false);
      }
    };
    getFunctions();
  }, []);

  const columns: GridColDef<FunctionsDataType>[] = [
    {
      field: "titulo",
      headerName: "Título",
      flex: 1,
      minWidth: 150,
      editable: false,
    },
    {
      field: "descricao",
      headerName: "Descrição",
      flex: 2,
      minWidth: 250,
      editable: false,
    },
    {
      field: "editarButton",
      headerName: "Ações",
      width: 80,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<FunctionsDataType>) => {
        const handleClickEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          navigate(`/easyReportV2/perfilRelatorios/funcao/${params.row.id}`);
        };
        const handleClickDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          if (params.row.perfis && params.row.perfis.length > 0) {
            setWarningModal(true);
            setSelectedFunction(params.row ?? null);
            return;
          }
          setIdToDelete(params.row.id);
          setDeleteModal(true);
        };
        return (
          <>
            <IconButton aria-label="Editar função" onClick={handleClickEdit}>
              <EditOutlined color={"text.secondary" as "secondary"} />
            </IconButton>
            <IconButton
              aria-label="Excluir função"
              onClick={handleClickDelete}
              disabled={params.row.isDefault}
            >
              <DeleteOutline
                sx={{
                  color: params.row.isDefault
                    ? "disabled"
                    : theme.palette.error.main,
                }}
              />
            </IconButton>
          </>
        );
      },
    },
  ];

  const filteredRows = useMemo(() => {
    const search = searchValue.toLowerCase().trim();
    if (!search) return allFunctions;
    return allFunctions.filter((row) => {
      const matchesSearch =
        row.titulo.toLowerCase().includes(search) ||
        row.descricao.toLowerCase().includes(search);
      return matchesSearch;
    });
  }, [searchValue, allFunctions]);

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      const logsString = localStorage.getItem("logs");
      const logsLoginClienteId = logsString ? JSON.parse(logsString)?.id : "";
      await api.post("/easyReport/v2/deleteFunction", {
        id: idToDelete,
        logsLoginClienteId,
      });
      setAllFunctions((prev) => prev.filter((row) => row.id !== idToDelete));
      setIdToDelete("");
      setDeleteModal(false);
      notifyOnPage(
        "easyReportV2/perfilRelatorios/funcao",
        "Função excluída com sucesso!",
        "success",
        3000,
      );
    } catch (error) {
      setDeleteModal(false);
      notifyOnPage(
        "easyReportV2/perfilRelatorios/funcao",
        (error as any)?.response?.data?.error || "Erro ao deletar itens",
        "error",
        3000,
      );
    } finally {
      setDeleting(false);
    }
  };
  function CustomNoRowsOverlay() {
    return (
      <Box sx={styles.boxNoRowsOverlay}>
        <EmptyBox style={styles.iconUserNotFound} />
        <Typography color="text.disabled">
          Nenhum relatório até o momento
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={styles.pageContainer}>
      <Box sx={styles.headerContainer}>
        <Breadcrumbs
          separator={<Typography sx={styles.titleAndLinkPage}>/</Typography>}
        >
          <Button
            variant="text"
            onClick={() => navigate("/easyReportV2/perfilRelatorios")}
            sx={{
              ...styles.titleAndLinkPage,
              color: theme.palette.text.secondary,
              fontSize: 18,
              ":hover": {
                backgroundColor: "transparent",
                color: theme.palette.text.primary,
              },
            }}
          >
            Perfil de Relatório
          </Button>
          <Typography
            sx={{
              ...styles.titleAndLinkPage,
              color: theme.palette.text.primary,
            }}
          >
            Funções Personalizadas
          </Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={styles.container}>
        <Paper sx={styles.paper}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={styles.boxHeader}>
              <TextField
                size="small"
                value={searchValue}
                placeholder="Pesquisar por título ou descrição"
                onChange={(e) => setSearchValue(e.target.value)}
                fullWidth
                sx={{ width: { xs: "100%", md: "400px" } }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Search />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Button
                startIcon={<Add />}
                onClick={() =>
                  navigate("/easyReportV2/perfilRelatorios/novaFuncao")
                }
                sx={styles.button}
                variant="contained"
              >
                Nova Função
              </Button>
            </Box>

            <Box
              sx={{
                width: "100%",
                height: "73vh",
                overflow: "hidden",
                borderRadius: 2,
                border: `1px solid ${theme.palette.stroke}`,
              }}
            >
              <DataGrid
                rows={filteredRows || []}
                columns={columns}
                loading={loading}
                disableRowSelectionOnClick
                disableColumnMenu={true}
                pageSizeOptions={[10, 25, 50, 100]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0
                    ? "even-row"
                    : "odd-row"
                }
                sx={styles.dataGrid}
                slots={{
                  noRowsOverlay: CustomNoRowsOverlay,
                }}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: paginationModel.pageSize },
                  },
                }}
              />
            </Box>
          </Box>
        </Paper>
        <AlertModal
          openModal={deleteModal}
          setOpenModal={setDeleteModal}
          severity="delete"
          singleButton={false}
          confirmar={deleting}
          handleButton={handleDelete}
          textContent={
            "Se prosseguir com a operação, a função será movida para a lixeira e excluído permanentemente após 30 dias."
          }
          handleCancel={() => {
            setDeleteModal(false);
          }}
        />
        <AlertModal
          openModal={warningModal}
          setOpenModal={setWarningModal}
          severity="warning"
          singleButton={true}
          buttonText={"Entendi"}
          handleButton={() => setWarningModal(false)}
          textContent={`Não é possível excluir esta função, pois ela possui Perfis vinculados: ${selectedFunction?.perfis?.join(",")}`}
          handleCancel={() => {
            setWarningModal(false);
          }}
        />
      </Box>
    </Box>
  );
};

export default FunctionPage;
